import { casesActions } from "configs";
import {
  useSelector,
  useDispatch,
  deepOmit,
  useInitializeOfflineData
} from "duck";
import {
  useCreateMedicalCaseMutation,
  useUpdateMedicalCaseMutation,
  MedicalCasesDocument
} from "requests";
import { hooks } from "../../../../../duck";
import { parseStoredCase } from "./utils";

export const usePublish = (id: number) => {
  const dispatch = useDispatch();
  const medCase = useSelector(({ cases }) => cases[id].data);
  const updateRequired = useSelector(
    ({ cases }) => cases[id].status === "has_offline_changes"
  );

  const [createCase] = useCreateMedicalCaseMutation();
  const [updateCase] = useUpdateMedicalCaseMutation();

  const { reset } = hooks.useSelection();
  const listVariables = hooks.useListQueryVariables();

  const { loadProcedures } = useInitializeOfflineData();
  const { offlineCases, needSyncCases } = hooks.useOfflineCases();

  if (updateRequired) {
    return () => {
      const { createdAt, ...parsed } = deepOmit(
        parseStoredCase(medCase),
        "__typename"
      ) as ReturnType<typeof parseStoredCase>;

      const {
        patient: {
          /* Check-in */
          name,
          species,
          breed,
          dateOfBirth,
          sex,
          weight,
          weightMeasure,
          procedures,
          procedureDate,
          alerts,
          dropOff,
          /* Exam */
          vaccines,
          patientTests,
          treatment,
          examNote,
          currentMedications,
          preAnestheticExam,
          physicalExam,
          diagnosticTesting,
          /* TPLO */
          tibialPlateauLevelingOsteotomyPlanning,
          /* Dental Charting */
          generalAssessment,
          dentalChartings,
          dentalSketch,
          dentalSketchComments,
          /* Prescriptions */
          tablets,
          suspensions,
          /* Calculations */
          medicationCalculations,
          miscellaneousGroup,
          defibMedications,
          fluidBolus,
          regionalBlocks,
          calculationAgreement,
          /* Monitoring */
          monitoring,
          patientProcedures,
          fluidTemperatures,
          anestheticGases,
          vitals,
          vitalComment,
          postAnestheticVitalComment,
          postAnestheticVitals
        },
        ...restData
      } = parsed.client;

      const avatar = medCase.client.patient.image;
      const additionalPhotos = medCase.client.patient.generalAssessment?.additionalPhotos.map(
        photo => (typeof photo === "number" ? { _id: photo } : photo)
      );
      const radiographicFindingsPhotos = medCase.client.patient.generalAssessment?.radiographicFindingsPhotos.map(
        photo => (typeof photo === "number" ? { _id: photo } : photo)
      );

      return updateCase({
        refetchQueries: [
          { query: MedicalCasesDocument, variables: listVariables }
        ],
        variables: {
          input: {
            _id: medCase._id,
            ...parsed,
            authorizations: parsed.authorizations.map(
              ({ condition, ...auth }) => auth
            ),
            disclaimerProcedures: parsed.disclaimerProcedures.map(
              ({ description, title, ...proc }) => proc
            ),
            client: {
              ...restData,
              patient: {
                _id: medCase.client.patient._id,
                image:
                  avatar === null
                    ? null
                    : (avatar as any)?.base64Format
                    ? (avatar as any)
                    : undefined,
                name,
                species,
                breed,
                dateOfBirth,
                sex,
                weight,
                weightMeasure,
                alerts,
                procedureDate,
                procedures,
                dropOff,

                tibialPlateauLevelingOsteotomyPlanning,
                vaccines,
                patientTests,
                treatment,
                examNote,
                currentMedications,
                preAnestheticExam,
                physicalExam,
                diagnosticTesting,
                generalAssessment: {
                  ...generalAssessment,
                  additionalPhotos,
                  radiographicFindingsPhotos
                },
                ...((dentalChartings?.length || 0) > 0
                  ? { dentalChartings }
                  : {}),
                dentalSketch,
                dentalSketchComments,
                tablets,
                suspensions,
                medicationCalculations,
                miscellaneousGroup,
                defibMedications,
                calculationAgreement,
                fluidBolus,
                regionalBlocks,
                monitoring,
                patientProcedures,
                fluidTemperatures,
                anestheticGases,
                vitals,
                vitalComment,
                postAnestheticVitalComment,
                postAnestheticVitals
              }
            }
          }
        }
      }).then(() => {
        dispatch(casesActions.removeCase(id));
        reset();
        if ([...offlineCases, ...needSyncCases].length === 1) {
          loadProcedures();
        }
      });
    };
  }

  return () => {
    return createCase({
      refetchQueries: [
        { query: MedicalCasesDocument, variables: listVariables }
      ],
      variables: { input: parseStoredCase(medCase) }
    }).then(() => {
      dispatch(casesActions.removeCase(id));
      reset();
      if ([...offlineCases, ...needSyncCases].length === 1) {
        loadProcedures();
      }
    });
  };
};
